import React from 'react';

const PageLayout = ({ title, children }) => (
  <div className="w-full max-w-4xl mx-auto px-6 py-12 md:py-20 animate-in fade-in duration-500">
    <div className="mb-12">
      <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 tracking-tight">{title}</h1>
      <div className="w-20 h-1.5 bg-accent rounded-full"></div>
    </div>
    <div className="prose prose-lg dark:prose-invert max-w-none text-muted-foreground">
      {children}
    </div>
  </div>
);

export const About = () => (
  <PageLayout title="About Us">
    <p>
      Welcome to <strong>SuperTags</strong>, the ultimate tool for content creators looking to optimize their YouTube videos for maximum discoverability.
    </p>
    <p>
      Our mission is simple: <strong>Help creators grow</strong>. We believe that great content deserves to be seen, and metadata is the key to unlocking that visibility. By leveraging advanced AI and the official YouTube Data API, we provide accurate, high-performing tags and real-time ranking insights.
    </p>
    <h3>Why Choose Us?</h3>
    <ul>
      <li><strong>Speed:</strong> Generate optimized tags in seconds.</li>
      <li><strong>Accuracy:</strong> Data-backed suggestions relevant to your content.</li>
      <li><strong>Privacy:</strong> We don't track your personal usage data.</li>
      <li><strong>Free:</strong> Essential tools available for everyone.</li>
    </ul>
  </PageLayout>
);

export const Terms = () => (
  <PageLayout title="Terms of Service">
    <p>Last updated: January 2026</p>
    <p>
      By accessing and using SuperTags, you accept and agree to be bound by the terms and provision of this agreement.
    </p>
    <h3>1. Usage</h3>
    <p>
      You agree to use our tools for lawful purposes only. You must not use this service to generate misleading metadata or violate YouTube's Community Guidelines.
    </p>
    <h3>2. Disclaimer</h3>
    <p>
      The tools are provided "as is". We do not guarantee specific ranking results as YouTube's algorithm is complex and constantly changing.
    </p>
    <h3>3. Intellectual Property</h3>
    <p>
      All content generated by the AI becomes your property to use. The tool's design and code are owned by SuperTags.
    </p>
  </PageLayout>
);

export const Policy = () => (
  <PageLayout title="Privacy Policy">
    <p>
      Your privacy is important to us. It is SuperTags's policy to respect your privacy regarding any information we may collect from you across our website.
    </p>
    <h3>Data Collection</h3>
    <p>
      We do not store your personal search history on our servers. API Keys provided for the Rank Tracker are stored locally on your device (in LocalStorage) and are never transmitted to our servers—they are sent directly to the YouTube API.
    </p>
    <h3>Cookies</h3>
    <p>
      We use local storage to save your preferences (like Dark Mode and API Key). We do not use third-party tracking cookies.
    </p>
  </PageLayout>
);

export const CookiePolicy = () => (
  <PageLayout title="Cookie Policy">
    <p>Last updated: January 2026</p>
    <p>
      This Cookie Policy explains how SuperTags uses cookies and similar technologies to recognize you when you visit our website.
    </p>
    <h3>What are cookies?</h3>
    <p>
      Cookies are small data files that are placed on your computer or mobile device when you visit a website. We use cookies to enable certain features of our application and to remember your preferences.
    </p>
    <h3>How we use cookies</h3>
    <ul>
      <li><strong>Essential Cookies:</strong> These are required for the website to function properly.</li>
      <li><strong>Preference Cookies:</strong> These allow us to remember choices you make, such as your preferred theme (Light/Dark mode) or securely saving your API keys locally on your device.</li>
    </ul>
    <p>We do not use third-party tracking or advertising cookies. All data remains within your browser to respect your privacy.</p>
  </PageLayout>
);

export const Disclaimer = () => (
  <PageLayout title="Disclaimer">
    <p>
      SuperTags is not affiliated, associated, authorized, endorsed by, or in any way officially connected with YouTube, Google, or any of their subsidiaries or its affiliates.
    </p>
    <p>
      The official YouTube website can be found at <a href="https://www.youtube.com" target="_blank" rel="noreferrer">https://www.youtube.com</a>.
    </p>
    <p>
      The term "YouTube" as well as related names, marks, emblems and images are registered trademarks of their respective owners.
    </p>
  </PageLayout>
);
